import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSubscriptionAccess } from '@/hooks/useSubscriptionAccess';
import { useAuth } from '@/hooks/useAuth';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';

interface SubscriptionGuardProps {
  children: ReactNode;
}

/**
 * Guard component that checks subscription access before rendering children.
 * Redirects to billing page if user doesn't have access.
 * 
 * Access rules:
 * - trialing: Full access (7-day trial period)
 * - active: Full access
 * - past_due: Full access during 7-day grace period
 * - canceled/unpaid/incomplete: No access, redirect to billing
 * - none (no subscription): No access, redirect to billing
 */
export function SubscriptionGuard({ children }: SubscriptionGuardProps) {
  const { hasAccess, isLoading, isError } = useSubscriptionAccess();
  const location = useLocation();

  // Show loading state while checking subscription
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // If error, allow access (fail open - better UX than blocking everyone)
  if (isError) {
    console.warn('Subscription check failed, allowing access');
    return <>{children}</>;
  }

  // Allow access to billing page regardless of subscription status
  if (location.pathname.startsWith('/billing')) {
    return <>{children}</>;
  }

  // Allow access to organization settings page regardless of subscription status
  if (location.pathname.startsWith('/organization')) {
    return <>{children}</>;
  }

  // Allow access to auth pages
  if (location.pathname.startsWith('/auth')) {
    return <>{children}</>;
  }

  // Allow access to public quote pages
  if (location.pathname.startsWith('/quote/')) {
    return <>{children}</>;
  }

  // If user has access, render children
  if (hasAccess) {
    return <>{children}</>;
  }

  // No access - redirect to billing page
  // Store the attempted URL for redirect after payment
  return <Navigate to="/billing" state={{ from: location.pathname }} replace />;
}

/**
 * Higher-order component version for route protection
 */
export function withSubscriptionGuard<P extends object>(
  Component: React.ComponentType<P>
) {
  return function SubscriptionGuardedComponent(props: P) {
    return (
      <SubscriptionGuard>
        <Component {...props} />
      </SubscriptionGuard>
    );
  };
}
